import { api, queryKeys, type User } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatNumber, statusTone } from '../../lib/format';
import { Chip, FormGroup, Input, StatCard, StatCards } from '../../ui';

export const TEAM_TABS = [
  { label: 'Direct', to: '/team/direct' },
  { label: 'Level', to: '/team/level' },
  { label: 'Referral List', to: '/team/referral-list' },
  { label: 'Binary Tree', to: '/team/binary' },
];

const col = createColumnHelper<User>();

/**
 * `offset` is the absolute row number of the first row on the current page
 * (0 on page 1), so "Sl No." counts continuously across pages rather than
 * restarting at 1 on every page.
 */
function buildTeamColumns(offset: number) {
  return [
    col.display({
      id: 'sl_no',
      header: 'Sl No.',
      cell: (info) => offset + info.row.index + 1,
    }),
    col.accessor('user_code', { header: 'User ID' }),
    col.accessor('name', { header: 'Name' }),
    col.accessor('status', {
      // Renamed from "Status" to "Account" — disambiguates account standing
      // (blocked/not) from package activation, which is its own column.
      header: () => (
        <span title="Account = your account is not blocked. Activated = you have paid the $100 package.">
          Account
        </span>
      ),
      cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
    }),
    col.accessor('is_activated', {
      header: () => (
        <span title="Activated = has paid the $100 joining package (independent of Account status).">
          Activated
        </span>
      ),
      cell: (i) => (i.getValue() ? <Chip tone="success">Yes</Chip> : <Chip tone="danger">No</Chip>),
    }),
    col.accessor('rank_code', { header: 'Rank', cell: (i) => i.getValue() ?? '—' }),
    col.accessor('created_at', { header: 'Joined', cell: (i) => formatDateTime(i.getValue()) }),
    col.accessor('activated_at', {
      header: 'Activation Date & Time',
      cell: (i) => formatDateTime(i.getValue()),
    }),
  ];
}

// Preserved export: ReferralList.tsx and LevelTeam.tsx import this static
// column set (no absolute Sl No. offset — out of scope for this change).
export const teamColumns = buildTeamColumns(0);

export function DirectTeam() {
  const { query, setParam, setPage } = useTableQuery();

  // Local, debounced mirror of the `q` URL param — typing shouldn't refetch
  // on every keystroke.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChange = (value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setParam('q', value), 300);
  };

  const stats = useQuery({
    queryKey: queryKeys.team('stats', null),
    queryFn: () => api.team.stats().then((r) => r.data),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.team('direct', query),
    queryFn: () => api.team.direct(query),
  });

  const offset = ((data?.meta?.current_page ?? 1) - 1) * (data?.meta?.per_page ?? 0);
  const columns = useMemo(() => buildTeamColumns(offset), [offset]);

  return (
    <>
      <PageHeader
        title="Direct Team"
        description="Members you personally sponsored. Directs 1 and 2 pay 50%; direct 3 onward pays 30%."
        tabs={TEAM_TABS}
      />

      <StatCards>
        <StatCard label="Directs" value={formatNumber(stats.data?.direct_count)} />
        <StatCard label="Active Directs" value={formatNumber(stats.data?.direct_active)} />
        <StatCard label="Total Team" value={formatNumber(stats.data?.total_team)} />
      </StatCards>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <FormGroup label="Search (User ID or Name)">
            <Input
              placeholder="Search…"
              defaultValue={query.q ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </FormGroup>
        </div>
        <div style={{ flex: '0 1 180px', minWidth: 160 }}>
          <FormGroup label="Joined From">
            <Input
              type="date"
              value={query.from ?? ''}
              onChange={(e) => setParam('from', e.target.value)}
            />
          </FormGroup>
        </div>
        <div style={{ flex: '0 1 180px', minWidth: 160 }}>
          <FormGroup label="Joined To">
            <Input
              type="date"
              value={query.to ?? ''}
              onChange={(e) => setParam('to', e.target.value)}
            />
          </FormGroup>
        </div>
      </div>

      <DataTable<User>
        title="Direct Members"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        emptyLabel="You have no direct members yet"
        onPageChange={setPage}
      />
    </>
  );
}
